import { useState } from 'react'
import { CalendarIcon } from '@/ui/icons'
import { format } from 'date-fns'
import { Calendar } from '@/ui/components'
import { Popover, PopoverContent, PopoverTrigger } from '@/ui/components'
import { Button } from '@/ui/components'
import { formatDateForDisplay } from '../../utils/dateHelpers'
import { cn } from '@/shared/utils'

interface TripDateRangePickerProps {
  startDate?: string
  endDate?: string
  onStartDateChange: (date: string) => void
  onEndDateChange: (date: string) => void
  className?: string
}

export function TripDateRangePicker({
  startDate,
  endDate,
  onStartDateChange,
  onEndDateChange,
  className
}: TripDateRangePickerProps) {
  const [startOpen, setStartOpen] = useState(false)
  const [endOpen, setEndOpen] = useState(false)

  const startDateObj = startDate ? new Date(startDate) : undefined
  const endDateObj = endDate ? new Date(endDate) : undefined

  const handleStartDateSelect = (date: Date | undefined) => {
    if (date) {
      onStartDateChange(format(date, 'yyyy-MM-dd'))
      setStartOpen(false)
    }
  }

  const handleEndDateSelect = (date: Date | undefined) => {
    if (date) {
      onEndDateChange(format(date, 'yyyy-MM-dd'))
      setEndOpen(false)
    }
  }

  return (
    <div className={cn("grid grid-cols-1 md:grid-cols-2 gap-6", className)}>
      <div>
        <label className="block text-sm font-medium text-foreground mb-2">
          Start Date
        </label>
        <Popover open={startOpen} onOpenChange={setStartOpen}>
          <PopoverTrigger asChild>
            <Button
              variant="outline"
              className={cn(
                "w-full justify-start text-left font-normal",
                !startDate && "text-muted-foreground"
              )}
            >
              <CalendarIcon className="mr-2 h-4 w-4" />
              {startDate ? formatDateForDisplay(startDate) : (
                <span>Select start date</span>
              )}
            </Button>
          </PopoverTrigger>
          <PopoverContent className="w-auto p-0" align="start">
            <Calendar
              mode="single"
              selected={startDateObj}
              onSelect={handleStartDateSelect}
              disabled={(date) => {
                // Disable dates after end date if end date is set
                if (endDateObj) {
                  return date > endDateObj
                }
                return false
              }}
              initialFocus
            />
          </PopoverContent>
        </Popover>
      </div>

      <div>
        <label className="block text-sm font-medium text-foreground mb-2">
          End Date
        </label>
        <Popover open={endOpen} onOpenChange={setEndOpen}>
          <PopoverTrigger asChild>
            <Button
              variant="outline"
              className={cn(
                "w-full justify-start text-left font-normal",
                !endDate && "text-muted-foreground"
              )}
            >
              <CalendarIcon className="mr-2 h-4 w-4" />
              {endDate ? formatDateForDisplay(endDate) : (
                <span>Select end date</span>
              )}
            </Button>
          </PopoverTrigger>
          <PopoverContent className="w-auto p-0" align="start">
            <Calendar
              mode="single"
              selected={endDateObj}
              onSelect={handleEndDateSelect}
              disabled={(date) => {
                // Disable dates before start date if start date is set
                if (startDateObj) {
                  return date < startDateObj
                }
                return false
              }}
              initialFocus
            />
          </PopoverContent>
        </Popover>
      </div>
    </div>
  )
}

